import { SALES_WEEKS_PER_MONTH } from "../../../utils/weeklySalesUpload";
import { rowTranDateYmd } from "./dateParse";

/** @param {Date} d */
export function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** ISO week number (Monday-based week). @returns {string} e.g. "2026-W22" */
export function getISOWeekNumber(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

/** Monday–Sunday range for the week containing `date`. */
export function getWeekRangeForDate(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    weekNumber: getISOWeekNumber(monday),
    fromDate: toYmd(monday),
    toDate: toYmd(sunday),
  };
}

export function getCurrentWeekRange() {
  return getWeekRangeForDate(new Date());
}

export function getCurrentMonthRange(date = new Date()) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { fromDate: toYmd(start), toDate: toYmd(end) };
}

function parseTranDate(v) {
  if (!v) return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v;
  const s = String(v).trim();
  const iso = Date.parse(s);
  if (!Number.isNaN(iso)) return new Date(iso);
  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m) {
    let y = Number(m[3]);
    if (y < 100) y += 2000;
    const d = new Date(y, Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Infer week from min/max TranDate in rows; fallback to current week. */
export function inferWeekMetaFromRows(rows) {
  let min = null;
  let max = null;
  for (const row of rows || []) {
    const d = parseTranDate(row.tranDate);
    if (!d) continue;
    if (!min || d < min) min = d;
    if (!max || d > max) max = d;
  }
  if (min && max) {
    const fromDate = toYmd(min);
    const toDate = toYmd(max);
    return {
      weekNumber: getISOWeekNumber(min),
      fromDate,
      toDate,
      label: `${fromDate} → ${toDate}`,
    };
  }
  const cur = getCurrentWeekRange();
  return { ...cur, label: `${cur.fromDate} → ${cur.toDate}` };
}

export function formatWeekRangeLabel(fromDate, toDate) {
  if (!fromDate && !toDate) return "—";
  if (fromDate === toDate) return fromDate;
  return `${fromDate || "?"} → ${toDate || "?"}`;
}

/** Calendar Mon–Sun for ISO week label e.g. 2026-W22 */
export function isoWeekToRange(weekNumber) {
  const m = /^(\d{4})-W(\d{1,2})$/i.exec(String(weekNumber || "").trim());
  if (!m) return null;
  const year = Number(m[1]);
  const week = Number(m[2]);
  const jan4 = new Date(year, 0, 4);
  const day = jan4.getDay() || 7;
  const mondayWeek1 = new Date(jan4);
  mondayWeek1.setDate(jan4.getDate() - day + 1);
  const monday = new Date(mondayWeek1);
  monday.setDate(mondayWeek1.getDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { fromDate: toYmd(monday), toDate: toYmd(sunday) };
}

/** Human-readable week range for history / delete confirm. */
export function getDisplayWeekRange(log) {
  if (!log) return "—";
  const { fromDate, toDate, weekNumber } = log;
  if (fromDate && toDate && fromDate !== toDate) {
    return formatWeekRangeLabel(fromDate, toDate);
  }
  const iso = isoWeekToRange(weekNumber);
  if (iso) {
    const txn = fromDate || toDate;
    const txnNote = txn ? ` (file dates: ${txn})` : "";
    return `${formatWeekRangeLabel(iso.fromDate, iso.toDate)}${txnNote}`;
  }
  return formatWeekRangeLabel(fromDate, toDate);
}

export function isUploadedAtInMonth(uploadedAtIso, refDate = new Date()) {
  if (!uploadedAtIso) return false;
  const d = new Date(uploadedAtIso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getFullYear() === refDate.getFullYear() && d.getMonth() === refDate.getMonth();
}

export function salesImportMonthDocId(year, monthIndex) {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
}

/** Period key for a calendar month week slot (1–5), e.g. 2026-06-W3 */
export function monthWeekPeriodKey(year, monthIndex, weekOfMonth) {
  return `${salesImportMonthDocId(year, monthIndex)}-W${weekOfMonth}`;
}

/** YYYY-MM-DD from calendar parts (no local timezone shift). */
function calendarYmd(year, monthIndex, day) {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Mon–Sun style range within calendar month (days 1–7, 8–14, …). */
export function buildMonthWeekMeta(year, monthIndex, weekOfMonth) {
  const w = Number(weekOfMonth);
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const startDay = (w - 1) * 7 + 1;
  const endDay = Math.min(w * 7, lastDay);
  const fromDate = calendarYmd(year, monthIndex, startDay);
  const toDate = calendarYmd(year, monthIndex, endDay);
  const periodKey = monthWeekPeriodKey(year, monthIndex, w);
  const monthLabel = new Date(year, monthIndex, 1).toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
  return {
    year,
    monthIndex,
    weekOfMonth: w,
    periodKey,
    weekNumber: periodKey,
    fromDate,
    toDate,
    label: `${monthLabel} · Week ${w} (${fromDate} → ${toDate})`,
  };
}

/** Full calendar month meta for monthly bulk upload. */
export function buildMonthMeta(year, monthIndex) {
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const fromDate = calendarYmd(year, monthIndex, 1);
  const toDate = calendarYmd(year, monthIndex, lastDay);
  const periodKey = salesImportMonthDocId(year, monthIndex);
  const monthLabel = new Date(year, monthIndex, 1).toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
  return {
    year,
    monthIndex,
    weekOfMonth: null,
    periodKey,
    weekNumber: periodKey,
    fromDate,
    toDate,
    label: `${monthLabel} (${fromDate} → ${toDate})`,
  };
}

/** Week slot (1–5) from normalized TranDate YYYY-MM-DD. */
export function weekOfMonthFromTranDateYmd(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || "").trim());
  if (!m) return null;
  const day = Number(m[3]);
  if (!day || day < 1) return null;
  return Math.min(SALES_WEEKS_PER_MONTH, Math.ceil(day / 7));
}

/** Split valid rows into week buckets for the selected calendar month. */
export function groupValidRowsByWeekOfMonth(rows, year, monthIndex) {
  const buckets = Object.fromEntries(
    Array.from({ length: SALES_WEEKS_PER_MONTH }, (_, i) => [i + 1, []])
  );
  const monthNum = monthIndex + 1;
  for (const row of rows || []) {
    if (row.valid === false) continue;
    const ymd = rowTranDateYmd(row);
    if (!ymd) continue;
    const [y, m] = ymd.split("-").map(Number);
    if (y !== year || m !== monthNum) continue;
    const w = weekOfMonthFromTranDateYmd(ymd);
    if (w) buckets[w].push(row);
  }
  return buckets;
}
